import { Divider, List } from '@fs/zion-ui'
import React from 'react'
import { useTranslation } from 'react-i18next'
import PersonDisplay from './PersonDisplay'
import NamesOnOtherImages from './NamesOnOtherImages'
import { formatRecordDisplayData, getHeadOfHouseholdName, getPrimaryPerson } from '../utils/record.utils'
import { sortPersonsByPrimary } from '../utils/display.utils'
import NoPrimaryAlert, { getNoPrimaryVariant } from '../../shared/components/NoPrimaryAlert'

/**
 * HouseholdDisplay
 *
 * Displays a household/record with its associated persons. Groups are told apart by a divider and
 * the Relationships card is the way into an existing group, so the region's aria-label names the group.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.household - The household object to display
 * @param {Array} props.persons - Array of person objects belonging to this household from the useHouseholdList hook
 * @param {Function} props.onPersonSelect - Callback when a person in the list is selected
 * @returns {JSX.Element}
 */
const HouseholdDisplay = ({ household, persons, onPersonSelect }) => {
  const [t] = useTranslation()

  const primaryPersonName = getHeadOfHouseholdName(household)
  const { primaryEvent } = formatRecordDisplayData(household)
  const hasPrimaryEvent = !!primaryEvent
  const hasPrimaryPerson = !!getPrimaryPerson(household)
  const sortedPersons = sortPersonsByPrimary(persons)

  const groupLabel = primaryPersonName
    ? t('household-display.group-region-with-name', '{primaryPersonName} group', { primaryPersonName })
    : t('household-display.group-region', 'Group')

  return (
    <section aria-label={groupLabel} data-testid="group-region">
      <Divider marginY="xxs" fullWidth />
      {/* The group's own divider supplies the leading gap above the alert. */}
      <NoPrimaryAlert variant={getNoPrimaryVariant({ hasPrimaryPerson, hasPrimaryEvent })} gutters="below" />
      <List>
        {sortedPersons.map((person) => (
          <PersonDisplay key={person.id} person={person} record={household} onPersonSelect={onPersonSelect} />
        ))}
        <NamesOnOtherImages household={household} />
      </List>
    </section>
  )
}

export default HouseholdDisplay
